import React, { useContext, useEffect, useRef, useState } from 'react';
import * as R from 'ramda';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import { useNavigate } from 'react-router-dom';

// Other Components
import { Box, Dialog, DialogContent, DialogTitle, Typography, useMediaQuery } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

// Utils
import { calculateExpectedSavings } from 'feature/loading-management/utils/calculate';
import { formatVolume } from 'utils/helpers/format';
import formatGraphData from '../utils/graphData';

// Context
import { SubscriptionsContext } from '../SubscriptionsContext';

// Components
import NavigationButtons from '../components/navigation-buttons/NavigationButtons';
import SubscriptionBundleCard from '../components/subscription-bundle-card/SubscriptionBundleCard';
import SubscriptionHeader from '../components/subscription-header/SubscriptionHeader';
import SubscriptionLegend from '../components/subscription-legend/SubscriptionLegend';
import SubscriptionBundleCardSkeleton from '../components/subscription-bundle-card/SubscriptionBundleCardSkeleton';
import UsageChart from '../components/usage-chart/UsageChart';

// Styles
import * as styles from './PlanSelection.styles';

const PlanSelection = props => {
  const {
    currentBaseRate,
    fetchBaseRate,
    fetchSubscriptionTierDetails,
    subscriptionTierDetailsLoading,
    subscriptionTiers,
  } = props;

  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const smUp = useMediaQuery(theme => theme.breakpoints.up('sm'), { noSsr: true });

  const cardContainerRef = useRef(null);
  const navigate = useNavigate();

  const {
    currentStep,
    data: { globalAffiliatesVolumes, globalCustomer, loadTierDetails, planSelected },
    initialData,
    setCurrentStep,
    setData,
  } = useContext(SubscriptionsContext);

  const [skeletonArray, setSkeletonArray] = useState([]);
  const [recommended, setRecommended] = useState(null);
  const [reccomendedTier, setRecommendedTier] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const summedVolume = globalAffiliatesVolumes?.volumes?.twelveMonthVolume?.summedVolume;
  const currentAnnualCost = parseInt((currentBaseRate * summedVolume).toFixed(0), 10);

  useEffect(() => {
    if (!R.isEmpty(subscriptionTiers)) {
      const values = subscriptionTiers?.map(({ overageRate, tierRate, maxThreshold }) =>
        calculateExpectedSavings(
          currentAnnualCost,
          summedVolume,
          overageRate,
          tierRate,
          maxThreshold
        )
      );
      // finding highest value from mapped array
      // max: best index so far
      // current: current element in arr
      // idx: index of current element in arr
      // arr: array we are using (values)

      // If the highest saving selection is a positive int
      // then set to that idx, otherwise don't set idx
      // this controls the recommended indicator
      const idxOfHighestSaving = values.reduce(
        (max, current, idx, arr) => (current > arr[max] ? idx : max),
        0
      );
      const highestSavings = values[idxOfHighestSaving] > 0 ? idxOfHighestSaving : null;
      setRecommended(highestSavings);
      setRecommendedTier(`Tier ${idxOfHighestSaving + 1}`);
    }
  }, [globalAffiliatesVolumes, currentAnnualCost, subscriptionTiers, summedVolume]);

  // determine # of skeletons to show based on width of the container
  useEffect(() => {
    const cardWidth = 304;
    const containerWidth = cardContainerRef?.current.getBoundingClientRect().width;
    const numberOfCardsToRender = R.clamp(1, 5, Math.ceil(containerWidth / cardWidth));
    setSkeletonArray(Array(numberOfCardsToRender).fill());
  }, []);

  // keeps routing and context in sync
  useEffect(() => {
    if (currentStep !== 2) setCurrentStep(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // get data to display in tier cards
  useEffect(() => {
    if (loadTierDetails && globalCustomer) {
      fetchBaseRate({ globalCustomer });
      fetchSubscriptionTierDetails();
      setData(prevData => ({ ...prevData, loadTierDetails: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTierSelect = plan => {
    if (plan.tierPlanName === planSelected.tierPlanName) {
      setData(prevData => ({
        ...prevData,
        planSelected: initialData.planSelected,
      }));
    } else {
      setData(prevData => ({ ...prevData, planSelected: plan }));
    }
  };

  const leftButtonClick = () => {
    navigate('../step1');
    setCurrentStep(1);
  };

  const rightButtonClick = () => {
    navigate(`../step3`);
    setCurrentStep(3);
  };

  return (
    <>
      <Box sx={styles.scroll}>
        <SubscriptionHeader
          disabled={!planSelected.contractID}
          handleLeftButtonClick={() => leftButtonClick()}
          handleRightButtonClick={() => rightButtonClick()}
          leftLabel="Back"
          rightLabel="Next"
          title="Select Your Swift+ Bundle"
        />
        <Box sx={styles.parentScroll}>
          <Box
            ref={cardContainerRef}
            sx={theme => styles.scrollContainer(theme, subscriptionTierDetailsLoading)}
          >
            {!subscriptionTierDetailsLoading && (
              <SubscriptionBundleCard
                annualCost={currentAnnualCost}
                currentUsage
                tierPlanName="Your Prior 12Mo Usage"
                tierRate={parseFloat(currentBaseRate)}
                volume={formatVolume(summedVolume)}
              />
            )}
            {subscriptionTierDetailsLoading
              ? skeletonArray?.map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <SubscriptionBundleCardSkeleton key={`card-skeleton-${index}`} />
                ))
              : subscriptionTiers?.map((tier, idx) => {
                  const { contractID, tierPlanName, maxThreshold, ...rest } = tier;
                  const formattedPlanName = `Tier ${idx + 1}`;

                  return (
                    <SubscriptionBundleCard
                      annualCost={currentAnnualCost}
                      annualVolume={summedVolume}
                      includesBOL={R.includes('5', R.toLower(tierPlanName))}
                      isRecommended={recommended === idx}
                      isSelected={formattedPlanName === planSelected?.tierPlanName}
                      key={contractID}
                      onClick={() => handleTierSelect({ ...tier, tierPlanName: formattedPlanName })}
                      openModal={setIsModalOpen}
                      tierPlanName={formattedPlanName}
                      volume={formatVolume(maxThreshold)}
                      volumeUsage={maxThreshold}
                      {...rest}
                    />
                  );
                })}
          </Box>
        </Box>
        <SubscriptionLegend />
        {!lgUp && (
          <NavigationButtons
            disabled={!planSelected.contractID}
            handleLeftButtonClick={() => leftButtonClick()}
            handleRightButtonClick={() => rightButtonClick()}
            leftLabel="Back"
            rightLabel="Next"
          />
        )}
      </Box>
      {isModalOpen && (
        <Dialog
          aria-labelledby="customized-dialog-title"
          onClose={() => setIsModalOpen(false)}
          open={isModalOpen}
          slotProps={{
            paper: { sx: styles.dialog },
          }}
        >
          <DialogTitle sx={styles.dialogTitle}>
            <>
              <Box display="flex" position="relative">
                <Typography sx={{ color: 'text.primary' }} variant="h6">
                  Historical Volume Usage
                </Typography>
                <IconButton
                  aria-label="close"
                  onClick={() => setIsModalOpen(false)}
                  size="large"
                  sx={{
                    color: 'graphic.icons',
                    padding: 0,
                    position: 'absolute',
                    right: 0,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              {recommended && (
                <Typography sx={{ marginTop: 2, maxWidth: 436 }} variant="body1">
                  Based on your historical volume usage, we recommend a {reccomendedTier}{' '}
                  enrollment.
                </Typography>
              )}
            </>
          </DialogTitle>
          <DialogContent sx={styles.dialogContent}>
            <UsageChart
              chartHeight={341}
              chartWidth={smUp ? 460 : 327}
              usageData={formatGraphData(globalAffiliatesVolumes)}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

PlanSelection.propTypes = {
  currentBaseRate: number,
  fetchBaseRate: func.isRequired,
  fetchSubscriptionTierDetails: func.isRequired,
  globalAffiliatesVolumes: arrayOf(shape({})),
  subscriptionTierDetailsLoading: bool.isRequired,
  subscriptionTiers: arrayOf(shape({})),
};

PlanSelection.defaultProps = {
  currentBaseRate: null,
  globalAffiliatesVolumes: [],
  subscriptionTiers: [],
};

export default PlanSelection;
